import type { VisualEditorPage, VisualEditorWidget } from '../../types.ts';
import {
  type DrawerTab,
  CONTAINER_CHILD_TYPES,
  isLegacyFlatRepeatingField,
  escapeHtml,
} from './VisualEditorDrawerConstants.ts';
import { renderFieldInput } from './VisualEditorFieldRenderer.ts';

export interface TabRenderersContext {
  page: VisualEditorPage | null;
  selectedWidgetId: string | null;
  activeTab: DrawerTab;
  editedValues: Record<string, unknown>;
  pendingInsertion: {
    widgetId: string;
    position: 'before' | 'after';
    displayText: string;
  } | null;
  findWidgetRecursive: (id: string | null) => VisualEditorWidget | null;
  findParentWidget: (id: string) => VisualEditorWidget | null;
}

export function renderActiveTabContent(
  ctx: TabRenderersContext,
  widget?: VisualEditorWidget | null
): string {
  if (!ctx.page) return '';

  switch (ctx.activeTab) {
    case 'editor':
      return renderBlockEditor(ctx, widget);
    case 'tree':
      return renderWidgetTree(ctx);
    case 'add':
      return renderAddWidgetCatalog(ctx);
    case 'settings':
      return renderPageSettings(ctx);
  }
}

export function renderBlockEditor(
  ctx: TabRenderersContext,
  widget?: VisualEditorWidget | null
): string {
  if (!widget) {
    if (ctx.page) {
      return `
        <div class="ve-block-editor-container">
          <div class="ve-page-edit-banner" style="background:rgba(56,189,248,0.1); border:1px solid rgba(56,189,248,0.3); border-radius:8px; padding:12px 14px; margin-bottom:16px;">
            <div style="font-size:11px; font-weight:700; color:#38BDF8; font-family:'JetBrains Mono',monospace;">[ SCOPE // PAGE SETTINGS ]</div>
            <div style="font-size:13px; color:#F8FAFC; margin-top:2px;">Configure metadata for <strong>${ctx.page.displayText || 'Page'}</strong> or switch to <strong>➕ Add</strong> to insert widgets.</div>
          </div>
          ${renderPageSettings(ctx)}
        </div>
      `;
    }
    return `<p class="ve-catalog-desc">Select a widget from the Widgets tab or click on the page to begin editing.</p>`;
  }

  const parent = ctx.findParentWidget(widget.contentItemId);
  let breadcrumbsHtml = '';
  if (parent) {
    breadcrumbsHtml = `
      <nav class="ve-breadcrumbs-bar loumars-breadcrumbs-bar" aria-label="Breadcrumbs">
        <button type="button" class="ve-breadcrumb-item ve-breadcrumb-parent" data-parent-id="${parent.contentItemId}">
          <span class="ve-crumb-icon">❖</span> ${escapeHtml(parent.displayText || parent.contentType)}
        </button>
        <span class="ve-breadcrumb-separator">/</span>
        <span class="ve-breadcrumb-item active">
          <span class="ve-crumb-icon">✦</span> ${escapeHtml(widget.displayText || widget.contentType)}
        </span>
      </nav>
    `;
  }

  const containerConfig = widget.contentType
    ? CONTAINER_CHILD_TYPES[widget.contentType]
    : undefined;
  const hasSubItems =
    !!containerConfig || (widget.subItems !== undefined && widget.subItems.length > 0);

  let fieldsHtml = '';
  for (const field of widget.fields) {
    if (containerConfig && isLegacyFlatRepeatingField(field.key)) {
      continue;
    }
    fieldsHtml += renderFieldInput(field, ctx.editedValues);
  }

  let flowItemsHtml = '';
  if (hasSubItems) {
    const subItems = widget.subItems || [];
    const isMenu =
      widget.contentType === 'Nav' ||
      widget.contentType === 'Navbar' ||
      widget.contentType === 'Footer' ||
      widget.contentType === 'Menu';
    const childType = containerConfig?.childType || (isMenu ? 'LinkMenuItem' : 'FeatureItem');
    const childLabel = containerConfig?.label || (isMenu ? 'Link' : 'Item');
    const childIcon = containerConfig?.icon || (isMenu ? '☍' : '✦');

    flowItemsHtml = `
      <div class="ve-flow-items-section loumars-flow-items-section">
        <div class="ve-flow-header">
          <div class="ve-flow-cad-tag">[ ${isMenu ? 'MENU LINKS' : 'FLOW ITEMS'} // LIST (${subItems.length}) ]</div>
          <button type="button" class="ve-btn-add-subitem" id="ve-btn-add-subitem" data-parent-id="${widget.contentItemId}" data-sub-type="${childType}">
            + Add ${childLabel}
          </button>
        </div>
        <div class="ve-flow-items-list" id="ve-flow-items-list">
          ${subItems.length === 0 ? `<p class="ve-flow-empty-hint">No items yet. Click "+ Add ${childLabel}" to insert one.</p>` : ''}
          ${subItems
            .map(
              (sub) => `
            <div class="ve-flow-item-card" data-id="${sub.contentItemId}" data-parent-id="${widget.contentItemId}" draggable="true">
              <div class="ve-flow-item-left">
                <span class="ve-flow-drag-handle" title="Drag to reorder" aria-label="Drag to reorder">⋮⋮</span>
                <span class="ve-flow-item-icon">${childIcon}</span>
                <div class="ve-flow-item-info">
                  <div class="ve-flow-item-title">${escapeHtml(sub.displayText || sub.contentType)}</div>
                  <div class="ve-flow-item-meta">${escapeHtml(sub.contentType)}${sub.data?.Url ? ' • ' + escapeHtml(String(sub.data.Url)) : ''}</div>
                </div>
              </div>
              <div class="ve-flow-item-actions">
                <button type="button" class="ve-flow-btn-edit" data-id="${sub.contentItemId}" title="Edit item">Edit</button>
                <button type="button" class="ve-badge-btn flow-btn-up" data-id="${sub.contentItemId}" data-parent-id="${widget.contentItemId}" title="Move up">↑</button>
                <button type="button" class="ve-badge-btn flow-btn-down" data-id="${sub.contentItemId}" data-parent-id="${widget.contentItemId}" title="Move down">↓</button>
                <button type="button" class="ve-badge-btn flow-btn-del" data-id="${sub.contentItemId}" data-parent-id="${widget.contentItemId}" title="Delete">✕</button>
              </div>
            </div>
          `
            )
            .join('')}
        </div>
      </div>
    `;
  }

  return `
    <div class="ve-block-editor-container">
      ${breadcrumbsHtml}
      <div class="ve-form-group">
        <div class="ve-label-row">
          <label class="ve-label" for="ve-input-displayText">${parent ? 'Item Display Name' : 'Block Display Name'}</label>
          <span class="ve-cad-key">System // DisplayText</span>
        </div>
        <input type="text" id="ve-input-displayText" class="ve-input" value="${widget.displayText || ''}" data-key="DisplayText" />
      </div>
      ${fieldsHtml}
      ${flowItemsHtml}
    </div>
  `;
}

export function renderWidgetTree(ctx: TabRenderersContext): string {
  if (!ctx.page || ctx.page.widgets.length === 0) {
    return `<p class="ve-catalog-desc">No widgets found on this page.</p>`;
  }

  return `
    <div class="ve-widget-tree-list" id="ve-widget-tree-list" style="display:flex; flex-direction:column; gap:8px;">
      ${ctx.page.widgets
        .map(
          (w) => `
        <div class="ve-widget-tree-item ${w.contentItemId === ctx.selectedWidgetId ? 'selected' : ''}" data-id="${w.contentItemId}" draggable="true">
          <div class="ve-tree-meta">
            <span class="ve-tree-drag-handle" title="Drag to reorder" aria-label="Drag to reorder">⋮⋮</span>
            <span class="ve-tree-icon">${w.contentType === 'Nav' ? '☍' : w.contentType === 'Footer' ? '▣' : '❖'}</span>
            <div>
              <div class="ve-tree-title-row" style="display:flex; align-items:center; gap:6px;">
                <span class="ve-tree-title">${escapeHtml(w.displayText || w.contentType)}</span>
                ${w.zone ? `<span class="ve-zone-badge ${w.zone.toLowerCase()}">${w.zone.toUpperCase()}</span>` : ''}
              </div>
              <div class="ve-tree-type">${escapeHtml(w.contentType)}${w.subItems?.length ? ` (${w.subItems.length} items)` : ''}</div>
            </div>
          </div>
          <div class="ve-tree-actions">
            <button type="button" class="ve-badge-btn tree-btn-up" data-id="${w.contentItemId}" title="Move up">↑</button>
            <button type="button" class="ve-badge-btn tree-btn-down" data-id="${w.contentItemId}" title="Move down">↓</button>
            <button type="button" class="ve-badge-btn tree-btn-del" data-id="${w.contentItemId}" title="Delete">✕</button>
          </div>
        </div>
        ${
          w.subItems && w.subItems.length > 0
            ? `
          <div class="ve-tree-subitems" style="padding-left: 20px; display: flex; flex-direction: column; gap: 4px; margin-top: -4px; margin-bottom: 4px;">
            ${w.subItems
              .map(
                (sub) => `
              <div class="ve-widget-tree-item subitem ${sub.contentItemId === ctx.selectedWidgetId ? 'selected' : ''}" data-id="${sub.contentItemId}" data-parent-id="${w.contentItemId}">
                <div class="ve-tree-meta">
                  <span class="ve-tree-sub-connector">└──</span>
                  <span class="ve-tree-icon sub-icon">${w.contentType === 'Nav' || w.contentType === 'Footer' ? '☍' : '✦'}</span>
                  <div>
                    <div class="ve-tree-title" style="font-size:12px;">${escapeHtml(sub.displayText || sub.contentType)}</div>
                    <div class="ve-tree-type" style="font-size:10px;">${escapeHtml(sub.contentType)}</div>
                  </div>
                </div>
                <div class="ve-tree-actions">
                  <button type="button" class="ve-badge-btn tree-btn-del" data-id="${sub.contentItemId}" data-parent-id="${w.contentItemId}" title="Delete">✕</button>
                </div>
              </div>
            `
              )
              .join('')}
          </div>
        `
            : ''
        }
      `
        )
        .join('')}
    </div>
  `;
}

export function renderAddWidgetCatalog(ctx: TabRenderersContext): string {
  if (!ctx.page) return '';

  let insertionBannerHtml = '';
  if (ctx.pendingInsertion) {
    insertionBannerHtml = `
      <div class="ve-insertion-banner loumars-insertion-banner" id="ve-insertion-banner">
        <div class="ve-insertion-banner-text">
          <span class="ve-insertion-tag">[ INSERTION // ${ctx.pendingInsertion.position.toUpperCase()}: ${escapeHtml(ctx.pendingInsertion.displayText.toUpperCase())} ]</span>
          <span class="ve-insertion-hint">Selecting a widget below will insert it <strong>${ctx.pendingInsertion.position}</strong> "${escapeHtml(ctx.pendingInsertion.displayText)}".</span>
        </div>
        <button type="button" class="ve-insertion-reset" id="ve-btn-insertion-reset" title="Cancel targeted insertion and add to end">✕ Reset</button>
      </div>
    `;
  }

  return `
    ${insertionBannerHtml}
    <div class="ve-catalog-grid">
      ${ctx.page.availableWidgetDefinitions
        .map(
          (def) => `
        <div class="ve-catalog-card" data-type="${def.contentType}">
          <div class="ve-catalog-icon">❖</div>
          <div class="ve-catalog-info">
            <div class="ve-catalog-title">${def.displayName}</div>
            <div class="ve-catalog-desc">${def.description}</div>
          </div>
        </div>
      `
        )
        .join('')}
    </div>
  `;
}

export function renderPageSettings(ctx: TabRenderersContext): string {
  if (!ctx.page) return '';

  return `
    <div class="ve-form-group">
      <label class="ve-label" for="ve-page-title">Page Navigation Title</label>
      <input type="text" id="ve-page-title" class="ve-input" value="${ctx.page.displayText || ''}" />
    </div>

    <div class="ve-form-group">
      <label class="ve-label" for="ve-seo-title">SEO Meta Title</label>
      <input type="text" id="ve-seo-title" class="ve-input" value="${ctx.page.pageTitle || ''}" />
    </div>

    <div class="ve-form-group">
      <label class="ve-label" for="ve-seo-desc">SEO Meta Description</label>
      <textarea id="ve-seo-desc" class="ve-textarea">${ctx.page.metaDescription || ''}</textarea>
    </div>

    <div class="ve-form-group">
      <label class="ve-label" for="ve-page-path">Permalink URL Path</label>
      <input type="text" id="ve-page-path" class="ve-input" value="${ctx.page.path || ''}" />
    </div>

    <button type="button" class="ve-btn ve-btn-draft" id="ve-btn-save-settings" style="width:100%; margin-top:8px;">
      Save Page Settings
    </button>
  `;
}
